<template>
  <div class="qualimat-report-container">
    <div class="ion-text-center">
      <h4 class="text-muted">Historique</h4>
    </div>

    <ion-list>
      <ion-row>
        <strong>Chargements</strong>
      </ion-row>

      <div class="ion-text-center mt-8" v-if="!previousDeliveries.length">
        <ion-label>
          Aucun historique de chargement disponible pour le moment
        </ion-label>
      </div>

      <template v-else>
        <ion-item
          button
          v-for="(delivery, index) in previousDeliveries"
          :key="index"
          @click="openShareUrl(delivery)"
        >
          <ion-label>
            <h2 v-if="delivery.all_materials && delivery.all_materials.length">
              {{ delivery.all_materials.join(', ') }}
            </h2>
            <h2 v-else>Materiaux non renseignée</h2>

            <p>{{ formatDate(delivery.created_at) }}</p>
          </ion-label>

          <ion-icon v-if="!isIos" slot="end" :icon="arrowForward"></ion-icon>
        </ion-item>
      </template>
    </ion-list>

    <ion-list class="ion-margin-top">
      <ion-row>
        <strong>Dernier lavage</strong>
      </ion-row>

      <div class="ion-text-center mt-8" v-if="!lastReport">
        <ion-label>Aucun lavage enregistré pour le moment</ion-label>
      </div>

      <ion-item v-else>
        <ion-label>
          <h2>Lavage type {{ lastReport.kind }}</h2>
          <p>{{ formatDate(lastReport.achieved_at) }}</p>
        </ion-label>
      </ion-item>
    </ion-list>

    <div
      v-if="driverLastReports && driverLastReports.length"
      class="driver-washes ion-text-center"
    >
      <h4 class="text-muted">Mes lavages</h4>

      <ion-accordion-group>
        <ion-accordion value="my-reports">
          <ion-item slot="header">
            <ion-label class="ion-text-left">
              <h2>Afficher</h2>
              <p>Lavages effectués sur cette benne pour la journée</p>
            </ion-label>
          </ion-item>

          <div slot="content">
            <ion-list>
              <ion-item
                button
                v-for="(report, index) in driverLastReports"
                :key="index"
                @click="editReport(report)"
              >
                <ion-label>
                  <h2>Lavage type {{ report.kind }}</h2>
                  <p v-if="report.product">{{ report.product }}</p>

                  <p>{{ formatDate(report.achieved_at) }}</p>
                </ion-label>

                <ion-icon
                  slot="end"
                  :icon="trash"
                  color="danger"
                  @click.stop="confirmDeleteReport(report)"
                ></ion-icon>
                <ion-icon
                  v-if="!isIos"
                  slot="end"
                  :icon="arrowForward"
                ></ion-icon>
              </ion-item>
            </ion-list>
          </div>
        </ion-accordion>
      </ion-accordion-group>
    </div>

    <div class="fab-custom" @click="createReport()">
      <label>Créer un lavage</label>
    </div>
  </div>
</template>

<script>
import {
  IonLabel,
  IonIcon,
  IonList,
  IonItem,
  IonAccordion,
  IonAccordionGroup,
  alertController,
  isPlatform,
} from '@ionic/vue';
import { getParams } from '@/services/users';
import axios from 'axios';
import { arrowForward, trash } from 'ionicons/icons';
import * as Sentry from '@sentry/vue';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { sendErrorToast, sendSuccessToast } from '@/services/utils/toastUtils';

dayjs.extend(utc);

export default {
  name: 'QualimatReport',
  components: {
    IonLabel,
    IonIcon,
    IonList,
    IonItem,
    IonAccordion,
    IonAccordionGroup,
  },

  props: {
    dumpBodyId: Number,
  },

  setup() {
    return {
      arrowForward,
      trash,
    };
  },

  data() {
    return {
      previousDeliveries: [],
      lastReport: null,
      driverLastReports: [],
    };
  },

  mounted() {
    this.fetchPreviousDeliveries();
  },

  computed: {
    isIos() {
      return isPlatform('ios');
    },
  },

  methods: {
    async fetchPreviousDeliveries() {
      try {
        const config = await getParams();

        const response = await axios.get('/driver/qualimat-report', {
          ...config,
          params: {
            dump_body_id: this.dumpBodyId,
          },
        });

        this.previousDeliveries = response.data.previousDeliveries || [];
        this.lastReport = response.data.lastReport || null;
        this.driverLastReports = response.data.driverLastReports || [];
      } catch (error) {
        if (process.env.VUE_APP_SENTRY_DSN) {
          Sentry.captureException(error);
        } else {
          console.error('Error fetching previous deliveries:', error);
        }

        sendErrorToast('Erreur lors de la récupération des données');
      }
    },

    openShareUrl(delivery) {
      if (!delivery || !delivery.share_url) return;

      window.open(delivery.share_url, '_blank');
    },

    formatDate(date) {
      if (!date) return '';

      // achieved_at is savec in UTC, so we need to convert it to local timezone before formatting it
      const formatted = dayjs
        .utc(date)
        // actual conversion to local timezone
        .local()
        .format('dddd D MMMM HH[h]mm');

      return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    },

    createReport() {
      this.$router.push(`/qualimat-reports/${this.dumpBodyId}`);
    },

    editReport(report) {
      this.$router.push(`/qualimat-reports/${this.dumpBodyId}/${report.id}`);
    },

    async confirmDeleteReport(report) {
      const alert = await alertController.create({
        header: 'Confirmation',
        message: 'Voulez-vous vraiment supprimer ce lavage ?',
        buttons: [
          {
            text: 'Annuler',
            role: 'cancel',
          },
          {
            text: 'Supprimer',
            handler: () => {
              this.deleteReport(report);
            },
          },
        ],
      });

      await alert.present();
    },

    async deleteReport(report) {
      try {
        const config = await getParams();

        await axios.delete(`/driver/qualimat-report/${report.id}`, config);

        this.fetchPreviousDeliveries();

        sendSuccessToast('Rapport supprimé avec succès');
      } catch (error) {
        if (process.env.VUE_APP_SENTRY_DSN) {
          Sentry.captureException(error);
        } else {
          console.error('Error deleting report:', error);
        }

        sendErrorToast('Erreur lors de la suppression du rapport');
      }
    },
  },
};
</script>

<style scoped lang="scss">
h4.text-muted {
  color: #6c757d;
}

h2 {
  color: #000;
}

.driver-washes {
  margin-top: 32px;
}

.qualimat-report-container {
  padding-bottom: 80px;
}

/* Sticky only for QualimatReport to ensure no changes for existing */
.fab-custom {
  position: fixed;
  // do not remove this version because we need it for safari
  position: -webkit-sticky;
  bottom: 16px;
  right: 16px;
  width: calc(100% - 32px);
}
</style>
